import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatCurrency, todayISO } from "@/lib/format";
import { FormShell, Field, CurrencyInput } from "@/components/forms/controls";
import EditableSelect from "@/components/EditableSelect";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const useList = (entity) => useQuery({ queryKey: ["entity", entity], queryFn: async () => (await api.get(`/${entity}`)).data });

// categories where linking a specific operation makes sense
const OP_RELEVANT = ["Machine Time/Labor", "QC/Inspection", "Heat Treatment", "Surface Treatment", "Welding"];

export default function AddCost() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const jobs = useList("jobs");
  const operations = useList("operations");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    job_id: "", cost_category: "", operation_id: "",
    planned_cost: "", actual_cost: "", date: todayISO(), remarks: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const selectableJobs = (jobs.data || []).filter((j) => ["Active", "Draft"].includes(j.job_status));
  const showOperation = OP_RELEVANT.includes(form.cost_category);
  const variance = (Number(form.actual_cost) || 0) - (Number(form.planned_cost) || 0);

  const submit = async () => {
    if (!form.job_id || !form.cost_category) {
      toast.error("Job and Cost Category are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!showOperation) payload.operation_id = "";
      const { data } = await api.post("/costs", payload);
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Cost ${data.code} added · Variance ${formatCurrency(data.cost_variance)}`);
      navigate(`/jobs/${form.job_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormShell title="Add Cost Entry" subtitle="Logs against a job and updates its Budget Spent automatically.">
      <Field label="Job (Active / Draft)" required>
        <Select value={form.job_id} onValueChange={(v) => set("job_id", v)}>
          <SelectTrigger data-testid="cost-job"><SelectValue placeholder="Select job..." /></SelectTrigger>
          <SelectContent>
            {selectableJobs.length === 0 ? <div className="px-3 py-2 text-sm text-slate-400">No active/draft jobs</div> :
              selectableJobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.code} · {j.job_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Cost Category" required>
        <EditableSelect optionsKey="cost_category" value={form.cost_category} onChange={(v) => set("cost_category", v)} testId="cost-category" />
      </Field>

      {showOperation && (
        <Field label="Operation" hint="Optional — link this cost to a specific operation">
          <Select value={form.operation_id} onValueChange={(v) => set("operation_id", v)}>
            <SelectTrigger data-testid="cost-operation"><SelectValue placeholder="Select operation (optional)..." /></SelectTrigger>
            <SelectContent>
              {(operations.data || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.operation_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Planned Cost"><CurrencyInput value={form.planned_cost} onChange={(v) => set("planned_cost", v)} testId="cost-planned" placeholder="0" /></Field>
        <Field label="Actual Cost"><CurrencyInput value={form.actual_cost} onChange={(v) => set("actual_cost", v)} testId="cost-actual" placeholder="0" /></Field>
      </div>

      <div className="flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
        <span className="text-sm font-medium text-slate-600">Variance (Actual − Planned)</span>
        <span className={`text-sm font-bold ${variance > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="cost-variance-preview">{formatCurrency(variance)}</span>
      </div>

      <Field label="Date"><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="cost-date" /></Field>
      <Field label="Remarks"><Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Optional" data-testid="cost-remarks" /></Field>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={() => navigate("/")} className="flex-1" data-testid="cost-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="cost-submit">
          {saving ? "Saving..." : "Add Cost Entry"}
        </Button>
      </div>
    </FormShell>
  );
}
